import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, MagnifyingGlass, Pencil, Plus, Upload, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useConfirm } from '@/components/ConfirmDialog';
import { resolve as resolveColor } from '@/lib/teamColor';
import { ApiError } from '@/api/client';
import {
  createLocation,
  deleteLocation,
  listLocations,
  updateLocation,
  type Location,
} from '@/api/locations';
import { LocationFormModal, type LocationFormSubmit } from '@/components/locations/LocationFormModal';
import { LocationImportDialog } from '@/components/locations/LocationImportDialog';

interface Props {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LocationsPanel({ slug, open, onOpenChange }: Props) {
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Location | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['locations', slug],
    queryFn: () => listLocations(token, slug, {}),
    enabled: !!slug && open,
  });

  const locations = useMemo(() => {
    const all = data?.locations ?? [];
    if (!search.trim()) return all;
    const q = search.trim().toLowerCase();
    return all.filter((l) => l.name.toLowerCase().includes(q));
  }, [data, search]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['locations', slug] });

  const createMut = useMutation({
    mutationFn: (input: LocationFormSubmit) =>
      createLocation(token, slug, {
        name: input.name,
        color: input.color,
        notes: input.notes,
        place_id: input.place?.place_id ?? '',
        formatted_address: input.place?.formatted_address ?? '',
        street: input.place?.street ?? '',
        postal_code: input.place?.postal_code ?? '',
        city: input.place?.city ?? '',
        country_code: input.place?.country_code ?? '',
        latitude: input.place?.latitude ?? null,
        longitude: input.place?.longitude ?? null,
        zones: input.zones,
      }),
    onSuccess: () => {
      setCreating(false);
      invalidate();
    },
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; input: LocationFormSubmit }) =>
      updateLocation(token, slug, args.id, {
        name: args.input.name,
        color: args.input.color,
        notes: args.input.notes,
        place_id: args.input.place?.place_id ?? '',
        formatted_address: args.input.place?.formatted_address ?? '',
        street: args.input.place?.street ?? '',
        postal_code: args.input.place?.postal_code ?? '',
        city: args.input.place?.city ?? '',
        country_code: args.input.place?.country_code ?? '',
        latitude: args.input.place?.latitude ?? null,
        longitude: args.input.place?.longitude ?? null,
      }),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteLocation(token, slug, id, ''),
    onSuccess: () => {
      // Delete is triggered from inside the edit modal; close it on success
      // alongside the standard cache invalidation.
      setEditing(null);
      invalidate();
    },
  });

  async function onDelete(loc: Location) {
    const ok = await confirm({
      title: t('locations.deleteConfirmTitle'),
      body: t('locations.deleteConfirmBody'),
      confirmLabel: t('locations.deleteLocation'),
      tone: 'destructive',
    });
    if (!ok) return;
    deleteMut.mutate(loc.id);
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-4 overflow-hidden p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border px-4 py-4">
          <SheetTitle>{t('locations.title')}</SheetTitle>
          <SheetDescription>{t('locations.tagline')}</SheetDescription>
        </SheetHeader>

        {/* Search filters the saved-locations library only — no Google
            Places lookup here. New venues are added via the footer
            "+ Lägg till plats" button, which opens the form modal where
            its own AddressAutocomplete handles geocoding. Keeping the
            two flows separate makes the panel feel like a quick library
            scan rather than a search-box-first creation surface. */}
        <div className="flex flex-col gap-3 px-4">
          <div className="relative flex items-center">
            <MagnifyingGlass
              aria-hidden
              className="pointer-events-none absolute left-3 size-4 text-foreground/50"
            />
            <Input
              aria-label={t('locations.search')}
              placeholder={t('locations.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('common.cancel')}
                onClick={() => setSearch('')}
                className="absolute right-1"
              >
                <X weight="bold" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {isLoading ? (
            <p className="text-sm text-foreground/60">{t('common.loading')}</p>
          ) : locations.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <MapPin weight="duotone" className="mx-auto mb-3 size-8 text-foreground/40" />
              <h2 className="font-heading text-base font-bold">{t('locations.empty.title')}</h2>
              <p className="mt-1 text-sm text-foreground/70">
                {search ? t('locations.empty.bodyNoMatch') : t('locations.empty.body')}
              </p>
            </div>
          ) : (
            <ul className="grid gap-2">
              {locations.map((loc) => {
                const palette = resolveColor(loc.name, loc.color);
                return (
                <li
                  key={loc.id}
                  className="group flex flex-col gap-2 rounded-xl border border-border bg-card p-3 shadow-xs"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2">
                      <span
                        aria-hidden
                        className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: palette.dot }}
                      />
                      <div className="min-w-0">
                        <h2 className="truncate font-heading text-base font-bold">{loc.name}</h2>
                        {loc.formatted_address ? (
                          <p className="mt-0.5 truncate text-sm text-foreground/70">{loc.formatted_address}</p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1 opacity-60 transition-opacity group-hover:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('locations.editLocation')}
                        onClick={() => setEditing(loc)}
                      >
                        <Pencil weight="bold" />
                      </Button>
                    </div>
                  </div>
                  {loc.zones.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {loc.zones.map((z) => (
                        <span
                          key={z.id}
                          className="rounded-pill bg-muted px-2.5 py-0.5 text-xs font-semibold"
                        >
                          {z.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer — pinned to the bottom of the sheet so the
            "+ Lägg till plats" call-to-action is always reachable
            regardless of how the saved-locations list scrolls. */}
        <div className="flex flex-col gap-2 border-t border-border bg-card p-4">
          <Button onClick={() => setCreating(true)} className="w-full">
            <Plus weight="bold" />
            {t('locations.newLocation')}
          </Button>
          <Button variant="outline" onClick={() => setImporting(true)} className="w-full">
            <Upload weight="bold" />
            {t('locations.importLocations')}
          </Button>
        </div>

        </SheetContent>
      </Sheet>

      {/* Modals render OUTSIDE the Sheet — the LocationFormModal uses a
          native <dialog> with showModal(), which conflicts with the radix
          focus-trap when nested inside SheetContent. As siblings, the native
          dialog goes to the browser's top-layer cleanly above the sheet. */}
      {creating && (
        <LocationFormModal
          mode="create"
          onSubmit={(values) => createMut.mutateAsync(values)}
          onCancel={() => setCreating(false)}
          submitting={createMut.isPending}
          error={describeError(createMut.error, t)}
        />
      )}
      {editing && (
        <LocationFormModal
          mode="edit"
          initial={editing}
          slug={slug}
          token={token}
          onSubmit={(values) => updateMut.mutateAsync({ id: editing.id, input: values })}
          onCancel={() => setEditing(null)}
          onDelete={() => onDelete(editing)}
          submitting={updateMut.isPending}
          deleting={deleteMut.isPending}
          error={describeError(updateMut.error, t)}
        />
      )}
      {importing && (
        <LocationImportDialog slug={slug} onClose={() => setImporting(false)} />
      )}
    </>
  );
}

function describeError(err: unknown, t: (k: string) => string): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.code === 'name_taken') return t('locations.errors.nameTaken');
    if (err.code === 'zone_name_taken') return t('locations.errors.zoneNameTaken');
    if (err.code === 'zone_in_use') return t('locations.errors.zoneInUse');
  }
  return t('locations.errors.generic');
}
